import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mic2, Check, RotateCcw, Sparkles, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";

// Pannello (solo admin) per scegliere la voce dell'agente delle chiamate: preset + voci registrate/clonate + default.
export const VoiceAgentPicker = ({ onChange }) => {
  const [opts, setOpts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // chiave opzione in corso

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/voice/agent-voice/options");
      setOpts(data);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const after = async (msg) => { toast.success(msg); await load(); onChange?.(); };

  const applyPreset = async (p) => {
    setBusy(`preset:${p.preset}`);
    try {
      await api.put("/voice/agent-voice", { preset: p.preset });
      await after(`L'agente ora parla con la voce «${p.nome}»`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(null); }
  };

  const applyRecorded = async (v) => {
    setBusy(`voice:${v.id}`);
    try {
      await api.post(`/mypailot/voices/${v.id}/agent`);
      await after(`L'agente ora parla con la voce di ${v.nome}`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(null); }
  };

  const resetDefault = async () => {
    setBusy("default");
    try {
      await api.put("/voice/agent-voice", { default: true });
      await after("L'agente è tornato alla voce di default");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(null); }
  };

  const cur = opts?.attuale;
  const isPreset = (p) => cur?.tipo === "preset" && cur?.preset === p.preset;
  const isRecorded = (v) => cur?.voice_id === v.id;
  const isDefault = !cur;

  if (loading) {
    return <div className="surface flex items-center justify-center p-8" data-testid="agent-voice-loading"><Loader2 className="h-5 w-5 spin-slow text-primary" /></div>;
  }

  return (
    <section className="surface space-y-5 p-6" data-testid="agent-voice-picker">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#F5B942]/15 text-[#FCD990]"><Mic2 className="h-5 w-5" /></div>
        <div>
          <h2 className="text-base font-semibold md:text-lg">Voce delle chiamate</h2>
          <p className="text-xs text-muted-foreground">Vale per le chiamate in entrata e in uscita. Visibile e modificabile solo dall'amministratore.</p>
        </div>
      </div>

      <p data-testid="agent-voice-current" className="rounded-lg border border-border bg-background/60 px-4 py-3 text-sm">
        Voce attuale:{" "}
        {isDefault ? <span className="font-semibold text-white">Voce di default</span>
          : cur?.tipo === "preset" ? <span className="font-semibold text-[#FCD990]">{cur.nome} · voce predefinita</span>
          : <span className="font-semibold text-emerald-300">{cur?.nome} · voce registrata (MyPailot)</span>}
      </p>

      {/* Preset */}
      <div>
        <p className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground"><Sparkles className="h-3.5 w-3.5" /> Voci predefinite</p>
        <ul className="grid gap-3 sm:grid-cols-2" data-testid="agent-voice-presets">
          {opts.preset.map((p) => (
            <li key={p.preset} data-testid={`preset-${p.preset}`} className={`flex items-center justify-between gap-3 rounded-xl border p-4 transition-colors ${isPreset(p) ? "border-[#F5B942]/50 bg-[#F5B942]/10" : "border-border bg-background/40"}`}>
              <div className="min-w-0">
                <p className="font-semibold">{p.nome}</p>
                <p className="truncate text-xs text-muted-foreground">{p.descrizione}</p>
              </div>
              {isPreset(p) ? (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#F5B942]/20 px-3 py-1.5 text-xs font-semibold text-[#FCD990]"><Check className="h-3.5 w-3.5" /> In uso</span>
              ) : (
                <button data-testid={`use-preset-${p.preset}`} onClick={() => applyPreset(p)} disabled={!!busy} className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-white hover:bg-secondary disabled:opacity-50">
                  {busy === `preset:${p.preset}` ? <Loader2 className="h-3.5 w-3.5 spin-slow" /> : null} Usa
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Voci registrate dell'admin */}
      <div>
        <p className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground"><UserRound className="h-3.5 w-3.5" /> Le tue voci registrate</p>
        {opts.mypailot.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nessuna voce registrata. Vai in <Link to="/mypailot" className="text-primary hover:underline">MyPailot</Link> per registrare la tua voce e usarla per l'agente.</p>
        ) : (
          <ul className="space-y-2" data-testid="agent-voice-recorded">
            {opts.mypailot.map((v) => (
              <li key={v.id} data-testid={`recorded-${v.id}`} className={`flex items-center justify-between gap-3 rounded-xl border p-4 transition-colors ${isRecorded(v) ? "border-emerald-500/50 bg-emerald-500/10" : "border-border bg-background/40"}`}>
                <div className="min-w-0">
                  <p className="truncate font-semibold">{v.nome}{v.ruolo && <span className="ml-1 text-xs font-normal text-muted-foreground">· {v.ruolo}</span>}</p>
                  <p className="text-xs text-muted-foreground">{v.clonata ? "già clonata su ElevenLabs" : "verrà clonata al primo uso"}</p>
                </div>
                {isRecorded(v) ? (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-300"><Check className="h-3.5 w-3.5" /> In uso</span>
                ) : (
                  <button data-testid={`use-recorded-${v.id}`} onClick={() => applyRecorded(v)} disabled={!!busy} className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-white hover:bg-secondary disabled:opacity-50">
                    {busy === `voice:${v.id}` ? <Loader2 className="h-3.5 w-3.5 spin-slow" /> : null} Usa
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {!isDefault && (
        <button data-testid="agent-voice-reset" onClick={resetDefault} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-medium text-muted-foreground hover:text-white disabled:opacity-50">
          {busy === "default" ? <Loader2 className="h-3.5 w-3.5 spin-slow" /> : <RotateCcw className="h-3.5 w-3.5" />} Torna alla voce di default
        </button>
      )}
    </section>
  );
};
